import ProductListing from "@/components/ProductListing";
import SEO from "@/components/SEO";
import { searchApiFetcher, SearchResponse } from "@/lib/algolia";
import { SESSION_STORAGE_KEYS } from "@/lib/constants";
import { useAppDispatch } from "@/lib/redux/hooks";
import {
  ProductFilter,
  productFilterOptionAdapter,
  setAllProductFilters,
  updateManyProductFilterOptions,
  updateProductFilterOption,
} from "@/lib/redux/productFilters/productFiltersSlice";
import { setProductSearchQuery } from "@/lib/redux/productSearch/productSearchSlice";
import {
  addAppListener,
  productFilterOptionSelectors,
  productFilterSelectors,
  RootState,
} from "@/lib/redux/store";
import {
  getMainLayoutQuery,
  MainLayoutQuery,
  seoQuerySchema,
} from "@/lib/sanity/queries";
import { sanityClient } from "@/lib/sanity/server";
import { GetServerSideProps, NextPage } from "next";
import { groq } from "next-sanity";
import { useRouter } from "next/router";
import { ParsedUrlQuery } from "querystring";
import { useEffect } from "react";
import { SWRConfig, SWRConfiguration } from "swr";
import { unstable_serialize } from "swr/infinite";
import { z } from "zod";
import isbot from "isbot";

const filtersQueryResponseSchema = z.array(
  z.object({
    id: z.string(),
    name: z.string(),
    mainFilter: z.boolean().nullable(),
    values: z.array(z.array(z.string()).nullable()).nullable(),
  })
);

const filtersGroupsQueryResponseSchema = z.array(
  z.object({
    title: z.string(),
    groups: z.array(
      z.object({
        title: z.string(),
        hideTitle: z.boolean().nullable(),
      })
    ),
  })
);

type FiltersQueryResponse = z.infer<typeof filtersQueryResponseSchema>;

const getFilters = async () => {
  const query = groq`{
    "filters": [
      ...*[_type == "attribute" && filterVisible == true] | order(orderRank) {
        "id": _id,
        "name": name,
        "mainFilter": mainFilter,
        "values": *[_type == "product"].parameters[attribute._ref == ^._id].values
      }
    ],
    "groups": *[_type == "filterGroups" ] {
      title,
      "groups": groups[] {
        title,
        hideTitle,
      }
    }
  }`;
  const data = await sanityClient.fetch(query);
  const filters = filtersQueryResponseSchema.parse(data.filters);
  const groups = filtersGroupsQueryResponseSchema.parse(data.groups);

  return {
    filters: filters.map((filter) => {
      const values: string[] = [];
      filter.values?.forEach((arr) => {
        if (arr) {
          values.push(...arr);
        }
      });
      const dedup = Array.from(new Set(values));
      const sorted = dedup.sort((a, b) => a.localeCompare(b));
      return {
        ...filter,
        values: sorted,
      };
    }),
    groups,
  };
};

const catalogsQueryResponseSchema = z.array(
  z.object({
    slug: z.string(),
    title: z.string(),
    downloadURL: z.string().nullable(),
  })
);

type CatalogsQueryResponse = z.infer<typeof catalogsQueryResponseSchema>;

const getCatalogs = async (): Promise<CatalogsQueryResponse> => {
  const query = groq`*[_type == "catalog"]{
    "slug": slug.current,
    title,
    "downloadURL": pdf.asset->url
  }`;
  const data = await sanityClient.fetch(query);
  return catalogsQueryResponseSchema.parse(data);
};

const pageQueryResponseSchema = z.object({
  seo: seoQuerySchema,
});

type PageQueryResponse = z.infer<typeof pageQueryResponseSchema>;

const getPage = async (params: { locale: string; defaultLocale: string }) => {
  const query = groq`*[_id == "productListing"][0] {
    seo {
      "metaTitle": coalesce(metaTitle[$locale], metaTitle[$defaultLocale]),
      "metaDesc": coalesce(metaDesc[$locale], metaDesc[$defaultLocale]),
      "shareTitle": coalesce(shareTitle[$locale], shareTitle[$defaultLocale]),
      "shareDesc": coalesce(shareDesc[$locale], shareDesc[$defaultLocale]),
      shareGraphic {
        asset->{
          url,
          metadata {
            dimensions {
              width,
              height
            }
          }
        }
      }
    },
  }
  `;
  const data = await sanityClient.fetch(query, params);
  return pageQueryResponseSchema.parse(data);
};

type Props = {
  bot: boolean;
  layout: MainLayoutQuery;
  filters: {
    id: string;
    name: string;
    mainFilter: boolean | null;
    values: string[];
  }[];
  groups: {
    title: string;
    groups: {
      title: string;
      hideTitle: boolean | null;
    }[];
  }[];
  catalogs: CatalogsQueryResponse;
  page: PageQueryResponse;
  fallback: {
    [key: string]: [SearchResponse];
  };
};

export const getServerSideProps: GetServerSideProps<Props> = async ({
  query,
  defaultLocale = "it",
  locale = "it",
  req,
}) => {
  const bot = "bot" in query || isbot(req.headers["user-agent"]);
  // eslint-disable-next-line no-console
  if (bot) console.log("🤖 Request coming from bot");

  const [search, { filters, groups }, catalogs, page, layout] =
    await Promise.all([
      searchApiFetcher(query),
      getFilters(),
      getCatalogs(),
      getPage({
        defaultLocale,
        locale,
      }),
      getMainLayoutQuery({
        defaultLocale,
        locale,
      }),
    ]);
  const getKey = (index: number) => ({
    ...query,
    page: index,
  });
  return {
    props: {
      bot,
      layout,
      filters,
      groups,
      catalogs,
      page,
      fallback: {
        [unstable_serialize(getKey)]: [search],
      },
    },
  };
};

const config: SWRConfiguration = {
  fetcher: searchApiFetcher,
  revalidateOnMount: false,
  revalidateOnFocus: false,
  // dedupe requests with the same key in this time span in milliseconds
  dedupingInterval: 20000,
};

const Products: NextPage<Props> = (props) => {
  const { fallback, filters, groups, catalogs, page, bot } = props;

  const router = useRouter();
  const dispatch = useAppDispatch();

  useEffect(() => {
    const { q, ...rest } = router.query;

    if (q && typeof q === "string") {
      dispatch(setProductSearchQuery(q));
    }

    const payload: ProductFilter[] = filters.map((filter) => {
      const queryParam = rest[filter.id];
      const options = productFilterOptionAdapter.setAll(
        productFilterOptionAdapter.getInitialState(),
        filter.values.map((value) => ({
          value,
          selected:
            queryParam instanceof Array
              ? queryParam.includes(value)
              : queryParam === value,
        }))
      );
      return {
        id: filter.id,
        title: filter.name,
        main: filter.mainFilter === true,
        options,
      };
    });

    payload.push({
      id: "catalog",
      title: "Catalogo",
      main: false,
      options: productFilterOptionAdapter.setAll(
        productFilterOptionAdapter.getInitialState(),
        catalogs.map((catalog) => {
          const value = catalog.slug;
          const queryParam = rest["catalog"];
          return {
            value,
            selected:
              queryParam instanceof Array
                ? queryParam.includes(value)
                : queryParam === value,
          };
        })
      ),
    });

    dispatch(setAllProductFilters(payload));

    const syncRouter = (state: RootState) => {
      const q = state.productSearch?.query;
      const filters: ParsedUrlQuery = {};

      productFilterSelectors.selectAll(state).forEach((filter) => {
        filters[filter.id] = productFilterOptionSelectors
          .selectAll(filter)
          .filter((filter) => filter.selected)
          .map((option) => option.value);
      });

      const query = filters;
      if (q) {
        query.q = q;
      }

      router.push({ query }, undefined, {
        scroll: false, // prevent popcorn effect
        shallow: true, // prevent server hit
      });
    };

    // Store inside session storage the last
    // pathname executed. This is done to make
    // the `Go Back` link in the PDP redirect always
    // to the last search query.
    const handleRouteChange = (pathname: string) => {
      // We keep this check in case the route listener
      // doesn't unmount for some reason.
      if (pathname.startsWith("/products?")) {
        window.sessionStorage.setItem(
          SESSION_STORAGE_KEYS.LAST_PLP_STATE,
          pathname
        );
      }
    };
    // Remember to unsubscribe when component unmounts
    router.events.on("routeChangeComplete", handleRouteChange);

    const listeners = [
      addAppListener({
        actionCreator: updateProductFilterOption,
        /** Updates router query when a filter option is selected/unselected */
        async effect(action, listenerApi) {
          // Prevent concurrent effects
          listenerApi.cancelActiveListeners();
          // Skip if action didn't touch the `selected` prop.
          const { changes } = action.payload;
          if ("selected" in changes) {
            syncRouter(listenerApi.getState());
          }
        },
      }),
      addAppListener({
        actionCreator: updateManyProductFilterOptions,
        /** Updates router query when some of the filter options have been selected or unselected */
        async effect(action, listenerApi) {
          // Prevent concurrent effects
          listenerApi.cancelActiveListeners();
          // Skip if action didn't touch the `selected` prop.
          const { updates } = action.payload;
          if (updates.some((update) => "selected" in update.changes)) {
            syncRouter(listenerApi.getState());
          }
        },
      }),
      addAppListener({
        actionCreator: setProductSearchQuery,
        async effect(_, listenerApi) {
          // Prevent concurrent effects
          listenerApi.cancelActiveListeners();
          // Update router query
          syncRouter(listenerApi.getState());
        },
      }),
    ];

    // Unsubscribe events when navigating to another page
    const unsubscribers = listeners.map((listener) => dispatch(listener));
    return () => {
      router.events.off("routeChangeComplete", handleRouteChange);
      unsubscribers.forEach((unsubscribe) => unsubscribe());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  return (
    <>
      {page.seo && <SEO {...page.seo} slug="products" />}
      <SWRConfig
        value={{
          ...config,
          fallback,
        }}
      >
        <ProductListing catalogs={catalogs} groups={groups} bot={bot} />
      </SWRConfig>
    </>
  );
};

export default Products;
